import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Briefcase, Compass, GraduationCap, ShieldCheck, Rocket, LineChart,
  Layers, Boxes, ArrowUpRight, ArrowRight, CheckCircle2, Users,
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";

const MODULES = [
  { key: "emploi",         icon: Briefcase,      color: "var(--mod-emploi)" },
  { key: "freelance",      icon: Compass,        color: "var(--mod-freelance)" },
  { key: "formation",      icon: GraduationCap,  color: "var(--mod-formation)" },
  { key: "certification",  icon: ShieldCheck,    color: "var(--afr-primary)" },
  { key: "entrepreneur",   icon: Rocket,         color: "var(--mod-entrepreneur)" },
  { key: "investissement", icon: LineChart,      color: "var(--mod-investissement)" },
  { key: "innovation",     icon: Layers,         color: "var(--mod-coach-ai)" },
  { key: "collaboration",  icon: Boxes,          color: "var(--mod-talent)" },
];

const HIGHLIGHTS = [
  { icon: Users,       label: "8 groupes d'acteurs connectés" },
  { icon: CheckCircle2,label: "1 seul environnement de confiance" },
  { icon: ShieldCheck, label: "Passeport Numérique panafricain" },
];

export default function Ecosystems() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="afr-root" data-testid="afr-ecosystems-root">
      <SeoHead
        title="Les 8 écosystèmes"
        description="Explorez les 8 écosystèmes AFRICTAL — Emploi, Freelance, Formation, Certification, Entrepreneuriat, Investissement, Innovation & Collaboration — connectés dans un environnement panafricain de confiance."
        path="/ecosystemes"
        keywords="écosystèmes AFRICTAL, emploi Afrique, freelance, formation, certification, entrepreneuriat, investissement, innovation"
      />
      <PublicNav />

      <div style={{ padding: "1.2rem 2rem 0" }}>
        <nav className="afr-breadcrumb" aria-label="breadcrumb" data-testid="ecosystems-breadcrumb">
          <Link to="/" data-testid="ecosystems-crumb-home">{t("common.breadcrumb_home")}</Link>
          <span className="afr-breadcrumb__sep">/</span>
          <span className="afr-breadcrumb__current">{t("nav.ecosystems")}</span>
        </nav>
      </div>

      {/* HERO */}
      <header className="afr-hero afr-hero--split" data-testid="ecosystems-hero">
        <div className="afr-hero__grid">
          <div className="afr-hero__left">
            <div className="afr-hero__meta">
              <span className="afr-eyebrow">🌍 Écosystème panafricain intégré</span>
            </div>
            <h1 className="afr-hero__title">
              8 écosystèmes intégrés.<br />
              <em>1 seul environnement</em> pour bâtir votre parcours.
            </h1>
            <p className="afr-hero__lead">
              AFRICTAL articule les 8 domaines qui font la croissance économique du continent —
              <strong> emploi, freelancing, formation, certification, entrepreneuriat,
              investissement, innovation et collaboration</strong> — autour du Passeport
              Numérique panafricain. Chaque acteur avance dans le même environnement,
              sans friction, du premier apprentissage à la réalisation concrète.
            </p>

            <div className="afr-hero__cta-row" style={{ marginTop: "1.8rem", display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
              <Link to="/login?mode=signup" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="ecosystems-cta-signup">
                <Rocket size={16} /> Rejoindre l'écosystème
              </Link>
              <a href="#modules" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="ecosystems-cta-discover">
                Découvrir les 8 modules <ArrowRight size={14} />
              </a>
            </div>

            <div className="afr-flex-row" style={{ marginTop: "1.4rem", gap: "0.8rem", flexWrap: "wrap" }} data-testid="ecosystems-highlights">
              {HIGHLIGHTS.map((h) => (
                <span key={h.label} className="afr-badge"
                      style={{ padding: "0.4rem 0.8rem", fontSize: "0.8rem", background: "var(--afr-bg-muted)" }}>
                  <h.icon size={13} style={{ verticalAlign: "middle", color: "var(--afr-primary)", marginRight: 5 }} />
                  {h.label}
                </span>
              ))}
            </div>
          </div>

          {/* Illustration réseau : 8 modules connectés au Passeport central */}
          <div className="afr-hero__right" aria-hidden="true">
            <div className="afr-hero-scene" style={{ minHeight: 420 }}>
              <svg viewBox="0 0 480 420" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <radialGradient id="ecoGlow" cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor="rgba(0,150,64,0.14)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                </defs>
                <ellipse cx="240" cy="210" rx="220" ry="170" fill="url(#ecoGlow)" />

                {/* 8 lignes vers le centre */}
                {[
                  [70, 90], [240, 60], [410, 90], [440, 210],
                  [410, 330], [240, 360], [70, 330], [40, 210],
                ].map(([x, y], i) => (
                  <line key={i} x1={x} y1={y} x2="240" y2="210"
                        stroke="rgba(0,150,64,0.35)" strokeWidth="1.3" strokeDasharray="5 5" />
                ))}

                {/* Anneau central */}
                <circle cx="240" cy="210" r="52" fill="rgba(0,150,64,0.10)" />
                <circle cx="240" cy="210" r="34" fill="var(--afr-primary)" />
                <text x="240" y="205" textAnchor="middle" fill="#fff" fontSize="11" fontFamily="Outfit" fontWeight="800">PASSEPORT</text>
                <text x="240" y="220" textAnchor="middle" fill="#fff" fontSize="11" fontFamily="Outfit" fontWeight="800">AFRICTAL</text>

                {/* 8 nœuds modules */}
                {MODULES.map((m, i) => {
                  const positions = [
                    { cx: 70, cy: 90 },   { cx: 240, cy: 60 },
                    { cx: 410, cy: 90 },  { cx: 440, cy: 210 },
                    { cx: 410, cy: 330 }, { cx: 240, cy: 360 },
                    { cx: 70, cy: 330 },  { cx: 40, cy: 210 },
                  ];
                  const p = positions[i];
                  return (
                    <g key={m.key}>
                      <circle cx={p.cx} cy={p.cy} r="22" fill={m.color} opacity="0.18" />
                      <circle cx={p.cx} cy={p.cy} r="14" fill={m.color} />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </header>

      {/* MODULES — 8 écosystèmes */}
      <section id="modules" className="afr-section" data-testid="ecosystems-modules">
        <h2 className="afr-section__title">Les 8 écosystèmes AFRICTAL</h2>
        <p className="afr-section__lead">
          Chaque écosystème répond à un besoin réel du continent. Ensemble, ils forment
          l'infrastructure numérique du talent africain.
        </p>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.4rem", marginTop: "1.5rem" }}>
          {MODULES.map((m) => (
            <article
              key={m.key}
              className="afr-card"
              data-testid={`ecosystems-module-${m.key}`}
              style={{ display: "flex", flexDirection: "column", gap: "0.5rem", borderTop: `4px solid ${m.color}` }}
            >
              <div className="afr-persona__icon" style={{ background: `linear-gradient(135deg, ${m.color}, var(--afr-accent))` }}>
                <m.icon size={22} />
              </div>
              <div className="afr-card__badge" style={{ color: m.color }}>{t(`landing.ecosystems.cards.${m.key}.badge`)}</div>
              <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.15rem", margin: "0.2rem 0", lineHeight: 1.3 }}>
                {t(`landing.ecosystems.cards.${m.key}.title`)}
              </h3>
              <p className="afr-muted" style={{ fontSize: "0.94rem", lineHeight: 1.6 }}>
                {t(`landing.ecosystems.cards.${m.key}.desc`)}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* POURQUOI 1 SEUL ENVIRONNEMENT */}
      <section className="afr-section" data-testid="ecosystems-why">
        <h2 className="afr-section__title">Pourquoi tout au même endroit ?</h2>
        <p className="afr-section__lead" style={{ maxWidth: 780 }}>
          Aujourd'hui, un talent africain doit jongler entre 6 plateformes différentes pour
          apprendre, se faire reconnaître, décrocher un emploi, lever des fonds ou bâtir son
          entreprise. Chaque outil isolé freine l'action. AFRICTAL réunit ces parcours en
          <strong> un seul écosystème cohérent, panafricain et souverain</strong>.
        </p>

        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.2rem", marginTop: "1.5rem" }}>
          {[
            { title: "Continuité", desc: "Votre profil, vos compétences, vos réalisations vous suivent d'un module à l'autre." },
            { title: "Confiance", desc: "Un seul environnement vérifié, sécurisé, adossé aux universités et institutions partenaires." },
            { title: "Souveraineté", desc: "Vos données restent panafricaines. Le passeport vous appartient, il est portable et exportable." },
            { title: "Efficacité", desc: "Moins de friction, plus de temps pour ce qui compte : réaliser vos ambitions." },
          ].map((item) => (
            <article key={item.title} className="afr-card" data-testid={`ecosystems-why-${item.title.toLowerCase()}`}>
              <div className="afr-card__badge" style={{ color: "var(--afr-primary)" }}>{item.title}</div>
              <p className="afr-muted" style={{ fontSize: "0.94rem", lineHeight: 1.6, marginTop: "0.4rem" }}>{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* TRANSITION DOUCE VERS SOLUTIONS */}
      <section className="afr-section" data-testid="ecosystems-transition">
        <div
          className="afr-card"
          data-testid="ecosystems-transition-card"
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr",
            alignItems: "center",
            gap: "2rem",
            padding: "2.2rem",
            background: "linear-gradient(135deg, color-mix(in oklab, var(--afr-primary) 6%, var(--afr-surface)), var(--afr-surface))",
            borderColor: "color-mix(in oklab, var(--afr-primary) 25%, var(--afr-border))",
            borderRadius: 20,
          }}
        >
          <div>
            <span className="afr-eyebrow" style={{ color: "var(--afr-primary)" }}>Suite du parcours</span>
            <h2
              style={{
                fontFamily: "'Outfit',sans-serif",
                fontSize: "clamp(1.5rem, 2.6vw, 2rem)",
                letterSpacing: "-0.01em",
                margin: "0.4rem 0 0.6rem",
                color: "var(--afr-secondary)",
                lineHeight: 1.25,
              }}
            >
              Découvrez comment AFRICTAL répond à vos besoins
            </h2>
            <p className="afr-muted" style={{ fontSize: "1rem", lineHeight: 1.65, margin: 0 }}>
              Vous connaissez maintenant les 8 écosystèmes. Chaque solution AFRICTAL s'appuie
              sur cette architecture — et se traduit concrètement en accompagnement pour les
              talents, les entrepreneurs, les entreprises, les investisseurs et les universités.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem", alignItems: "flex-start" }}>
            <Link
              to="/solutions"
              className="afr-btn afr-btn--primary afr-btn--lg"
              data-testid="ecosystems-transition-cta"
              style={{ width: "100%", justifyContent: "center" }}
            >
              Explorer les Solutions <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/#faq"
              className="afr-btn afr-btn--ghost afr-btn--sm"
              data-testid="ecosystems-transition-faq"
              style={{ opacity: 0.85 }}
            >
              Voir les questions fréquentes
            </Link>
          </div>
        </div>
      </section>

      {/* CTA — Solutions page */}
      <section className="afr-section" data-testid="ecosystems-cta">
        <div className="afr-cta-banner">
          <div className="afr-cta-banner__text">
            <h2>Prêt à choisir votre parcours ?</h2>
            <p>
              Découvrez comment AFRICTAL accompagne concrètement chaque acteur : talents,
              entrepreneurs, entreprises, investisseurs et universités.
            </p>
          </div>
          <div className="afr-cta-banner__actions">
            <Link to="/solutions" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="ecosystems-cta-solutions">
              Voir les solutions <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/login?mode=signup"
              className="afr-btn afr-btn--outline afr-btn--lg"
              style={{ color: "#F7F5F0", borderColor: "rgba(247,245,240,0.3)" }}
              data-testid="ecosystems-cta-signup-2"
            >
              <Rocket size={16} /> Créer un compte
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
